import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import FbUsersService from "../../../services/FbUsersService";
import { useToastAlert } from "../../app/ToastAlert/ToastAlertProvider";
import { CLIENT_STEPS_QUERY_KEY } from "../../client-steps/ClientStepsCard";
import { CLIENT_BODY_MEASUREMENT_LIST_QUERY_KEY } from "../../card/ClientMeasurementsCard";
import { WORKOUT_HIGHLIGHTS_QUERY_KEY } from "../../card/WorkoutHighlights";
import { WEIGHT_MEASUREMENTS_QUERY_KEY } from "../../client-body-weight/ClientBodyWeightCard";
import { CLIENT_FORMS_CARD_QUERY_KEY } from "../../client-forms/ClientFormsCard";
import { CLIENT_WORKOUTS_LIST_QUERY_KEY } from "../../client-workouts/ClientWorkouts";
import { CLIENT_EXERCISES_LIST_QUERY_KEY } from "../../client-workouts/ClientExercises";
import { CLIENT_PROFILE_PROGRAMS_LIST_QUERY_KEY } from "../ClientDetailsPrograms";
import { CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY } from "../../card/ClientProgressPhotosCard";
import {
  CLIENT_NUTRITION_ENTRIES_QUERY_KEY,
  CLIENT_NUTRITION_TARGETS_QUERY_KEY,
} from "../../client-nutrition-targets/ClientNutritionCard";
import { COACH_CLIENT_GENERIC_QUERY_KEY } from "../../../wrappers/router/coach/CoachClientProfileWrapper";
import { predicateQueryKeyByParams } from "../../../hooks/useOptimisticUpdateMutation";

const SETUP_CLIENT_TOAST_ID = "setup-client-toast";

interface UseSampleSetupProps {
  clientId: number;
  username: string;
  onSuccess?: () => void;
}

interface InvalidateClientQueriesProps {
  queryClient: QueryClient;
  clientId: number;
  username: string;
}

const clientExactKeys = [
  CLIENT_STEPS_QUERY_KEY,
  CLIENT_BODY_MEASUREMENT_LIST_QUERY_KEY,
  WORKOUT_HIGHLIGHTS_QUERY_KEY,
  WEIGHT_MEASUREMENTS_QUERY_KEY,
];

const usernameKeys = [
  CLIENT_NUTRITION_ENTRIES_QUERY_KEY,
  CLIENT_NUTRITION_TARGETS_QUERY_KEY,
  COACH_CLIENT_GENERIC_QUERY_KEY,
];

const clientPredicateKeys = [
  CLIENT_FORMS_CARD_QUERY_KEY,
  CLIENT_WORKOUTS_LIST_QUERY_KEY,
  CLIENT_EXERCISES_LIST_QUERY_KEY,
  CLIENT_PROFILE_PROGRAMS_LIST_QUERY_KEY,
  CLIENT_PROGRESS_PHOTOS_PAGE_QUERY_KEY,
];

const invalidateClientQueries = (props: InvalidateClientQueriesProps) => {
  const { queryClient, clientId, username } = props;
  clientExactKeys.forEach((key) => {
    queryClient.invalidateQueries({
      queryKey: [key, { clientId }],
      exact: true,
    });
  });

  clientPredicateKeys.forEach((key) => {
    queryClient.invalidateQueries({
      queryKey: [key, { clientId }],
      exact: false,
      predicate: (query) =>
        predicateQueryKeyByParams(query.queryKey, { clientId }),
    });
  });

  usernameKeys.forEach((key) => {
    queryClient.invalidateQueries({
      queryKey: [key, { username }],
      exact: true,
    });
  });
};

export const useSampleSetup = ({ clientId, username }: UseSampleSetupProps) => {
  const queryClient = useQueryClient();
  const { showToastAlert, closeToastAlert } = useToastAlert();

  return useMutation({
    mutationFn: FbUsersService.sampleSetup,
    onMutate: () => {
      showToastAlert("loading", {
        toastId: SETUP_CLIENT_TOAST_ID,
        message: "Setting up sample client...",
      });
    },
    onSuccess: () => {
      showToastAlert("success", {
        message: "Successfully set up sample client",
      });

      invalidateClientQueries({ queryClient, clientId, username });
    },
    onError: () => {
      showToastAlert("error", {
        message: "Failed to set up sample client",
      });
    },
    onSettled: () => {
      closeToastAlert(SETUP_CLIENT_TOAST_ID);
    },
  });
};
